import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Mail,
  MapPin,
  ChevronRight,
  Play,
  PlaySquare,
  BriefcaseBusiness,
  FolderKanban,
  Wrench,
  Menu,
  Download,
  Phone,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { SkillFlipCard } from "@/components/SkillFlipCard";
import {
  candidateProfile,
  education,
  experienceItems,
  keyProjects,
  outroThumbnail,
  outroVideo,
  profileImage,
  quickStats,
  resumePdf,
  resumeSectionRail,
  skillGroups,
} from "@/data/resumeData";

const HCAPTCHA_SITE_KEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2";

const WEB3FORMS_SUBMIT_URL = "https://api.web3forms.com/submit";
const web3formsAccessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || "";

const sectionIcons = {
  welcome: PlaySquare,
  experience: BriefcaseBusiness,
  projects: FolderKanban,
  skills: Wrench,
  contact: Mail,
};

const SectionHeading = ({ sectionId, title, subtitle, aside, titleAddon }) => {
  return (
    <div
      className={`mb-6 flex flex-wrap gap-4 ${aside ? "items-center justify-between" : "items-start justify-between"}`}
      data-testid={`${sectionId}-section-header`}
    >
      <div className="max-w-3xl">
        <h2
          className={`font-heading text-3xl font-semibold text-gray-900 ${titleAddon ? "flex flex-wrap items-baseline gap-x-3 gap-y-1" : ""}`}
          data-testid={`${sectionId}-section-title`}
        >
          {titleAddon ? (
            <>
              <span>{title}</span>
              <span className="font-medium text-gray-600" data-testid={`${sectionId}-section-title-addon`}>
                {titleAddon}
              </span>
            </>
          ) : (
            title
          )}
        </h2>
        {subtitle ? (
          <p className="mt-2 text-sm text-gray-600 sm:text-base" data-testid={`${sectionId}-section-subtitle`}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {aside ? <div className="shrink-0">{aside}</div> : null}
    </div>
  );
};

export default function InteractiveResumeLayoutPage() {
  const [activeSectionId, setActiveSectionId] = useState("welcome");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasOutroStarted, setHasOutroStarted] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [hcaptchaToken, setHcaptchaToken] = useState("");
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState({ type: "idle", message: "" });
  const hcaptchaRef = useRef(null);
  const detailPanelRef = useRef(null);
  const outroVideoRef = useRef(null);
  const sectionRefs = useRef({});
  const sectionOrder = useMemo(() => resumeSectionRail.map((section) => section.id), []);
  const detailSectionClassName = "rounded-xl border border-gray-200 bg-white p-4 sm:p-5";

  const registerSectionRef = (sectionId) => (node) => {
    if (node) {
      sectionRefs.current[sectionId] = node;
    }
  };

  const handleSectionCardClick = (sectionId) => {
    setActiveSectionId(sectionId);
    setIsMobileMenuOpen(false);
    const sectionNode = sectionRefs.current[sectionId];
    if (sectionNode) {
      sectionNode.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleContactMeClick = () => {
    const sectionNode = sectionRefs.current.contact;
    if (sectionNode) {
      sectionNode.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleOutroPlay = () => {
    const videoNode = outroVideoRef.current;
    if (videoNode) {
      videoNode.play();
    }
  };

  const handleContactInputChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactStatus({ type: "error", message: "Please complete all required fields." });
      return;
    }

    if (!hcaptchaToken) {
      setContactStatus({ type: "error", message: "Please complete the CAPTCHA." });
      return;
    }

    if (!web3formsAccessKey) {
      setContactStatus({
        type: "error",
        message: "Contact form is not configured (missing REACT_APP_WEB3FORMS_ACCESS_KEY).",
      });
      return;
    }

    setIsContactSubmitting(true);
    setContactStatus({ type: "idle", message: "" });

    try {
      const response = await fetch(WEB3FORMS_SUBMIT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: web3formsAccessKey,
          subject: "New message from your resume site",
          from_name: "Resume Contact Form",
          name: contactForm.name.trim(),
          email: contactForm.email.trim(),
          message: contactForm.message.trim(),
          "h-captcha-response": hcaptchaToken,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      const errMsg =
        payload.message ||
        (payload.body && payload.body.message) ||
        "Unable to submit your message right now.";
      if (!response.ok || !payload.success) {
        throw new Error(errMsg);
      }

      setContactStatus({ type: "success", message: "Thanks for reaching out — message received." });
      setContactForm({ name: "", email: "", message: "" });
    } catch (error) {
      setContactStatus({
        type: "error",
        message: error.message || "Something went wrong. Please try again.",
      });
    } finally {
      setHcaptchaToken("");
      hcaptchaRef.current?.resetCaptcha();
      setIsContactSubmitting(false);
    }
  };

  const syncActiveSectionFromScroll = useCallback(() => {
    const panel = detailPanelRef.current;
    const isDesktopPanelScroll = window.innerWidth >= 1024 && panel;
    const sectionIds = sectionOrder;

    if (!sectionIds.length) {
      return;
    }

    let nextActiveId = sectionIds[0];

    if (isDesktopPanelScroll) {
      const marker = panel.scrollTop + panel.clientHeight * 0.22;

      sectionIds.forEach((sectionId) => {
        const node = sectionRefs.current[sectionId];
        if (node && node.offsetTop <= marker) {
          nextActiveId = sectionId;
        }
      });

      if (panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2) {
        nextActiveId = sectionIds[sectionIds.length - 1];
      }
    } else {
      const marker = window.scrollY + 180;

      sectionIds.forEach((sectionId) => {
        const node = sectionRefs.current[sectionId];
        if (!node) return;

        const top = node.getBoundingClientRect().top + window.scrollY;
        if (top <= marker) {
          nextActiveId = sectionId;
        }
      });
    }

    setActiveSectionId((prev) => (prev === nextActiveId ? prev : nextActiveId));
  }, [sectionOrder]);

  useEffect(() => {
    const panel = detailPanelRef.current;
    const handleScrollSpy = () => syncActiveSectionFromScroll();

    if (panel) {
      panel.addEventListener("scroll", handleScrollSpy, { passive: true });
    }
    window.addEventListener("scroll", handleScrollSpy, { passive: true });
    window.addEventListener("resize", handleScrollSpy);

    const frameId = window.requestAnimationFrame(handleScrollSpy);

    return () => {
      if (panel) {
        panel.removeEventListener("scroll", handleScrollSpy);
      }
      window.removeEventListener("scroll", handleScrollSpy);
      window.removeEventListener("resize", handleScrollSpy);
      window.cancelAnimationFrame(frameId);
    };
  }, [syncActiveSectionFromScroll]);

  useEffect(() => {
    const normalized =
      (window.location.pathname || "/").replace(/\/$/, "") || "/";
    if (normalized !== "/contact") {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setActiveSectionId("contact");
      setIsMobileMenuOpen(false);
      const node = sectionRefs.current.contact;
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, []);

  const renderWelcomeSection = () => (
    <div data-testid="welcome-section-content">
      <SectionHeading
        sectionId="welcome"
        title="Wesley J Adeyinka-Skold"
        subtitle=""
      />

      <div className="mb-5 flex flex-wrap gap-3" data-testid="welcome-contact-row-top">
        <button
          type="button"
          onClick={handleContactMeClick}
          className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 transition-colors hover:bg-blue-600 hover:text-white"
          data-testid="welcome-contact-me-button"
        >
          <Mail className="h-4 w-4" />
          Contact Me
        </button>
        <a
          href={`tel:${candidateProfile.phoneTel}`}
          className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 transition-colors hover:bg-blue-600 hover:text-white"
          data-testid="welcome-call-phone-link"
        >
          <Phone className="h-4 w-4" />
          {candidateProfile.phoneDisplay}
        </a>
        <span
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm"
          data-testid="welcome-location-chip"
        >
          <MapPin className="h-4 w-4 text-blue-700" />
          {candidateProfile.location}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12" data-testid="welcome-main-grid">
        <Card className="border-gray-200 bg-white xl:col-span-5" data-testid="welcome-intro-card">
          <CardHeader>
            <CardTitle className="font-heading text-3xl text-gray-900" data-testid="welcome-candidate-title">
              {candidateProfile.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed text-gray-700 sm:text-base" data-testid="welcome-tagline-text">
              {candidateProfile.tagline}
            </p>
          </CardContent>
        </Card>

        <div className="xl:col-span-7" data-testid="welcome-photo-panel">
          <div
            className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            data-testid="welcome-photo-card"
          >
            <div
              className="relative aspect-[4/5] max-h-[480px] w-full overflow-hidden rounded-xl bg-gradient-to-b from-gray-100 to-gray-200 ring-1 ring-blue-100/60"
              data-testid="welcome-photo-frame"
            >
              <img
                src={profileImage.url}
                alt={profileImage.alt}
                className="h-full w-full object-cover object-top"
                loading="eager"
                data-testid="welcome-intro-photo"
              />
            </div>
          </div>

          <div
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3"
            data-testid="welcome-quick-stats-grid"
          >
            {quickStats.map((stat) => (
              <Card key={stat.id} className="border-gray-200 bg-white" data-testid={`welcome-stat-card-${stat.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-2xl text-blue-700" data-testid={`welcome-stat-value-${stat.id}`}>
                    {stat.value}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p
                    className="text-xs uppercase tracking-[0.18em] text-gray-500"
                    data-testid={`welcome-stat-label-${stat.id}`}
                  >
                    {stat.label}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderExperienceSection = () => (
    <div data-testid="experience-section-content">
      <SectionHeading
        sectionId="experience"
        title="Professional Experience"
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-testid="experience-cards-grid">
        {experienceItems.map((item) => (
          <Card
            key={item.id}
            className="border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            data-testid={`experience-card-${item.id}`}
          >
            <CardHeader>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500" data-testid={`experience-period-${item.id}`}>
                {item.period}
              </p>
              <CardTitle className="font-heading text-xl text-gray-900" data-testid={`experience-role-${item.id}`}>
                {item.role}
              </CardTitle>
              <p className="text-sm text-blue-700" data-testid={`experience-company-${item.id}`}>
                {item.company}
              </p>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700" data-testid={`experience-highlight-${item.id}`}>
                {item.highlight}
              </p>

              <ul className="mt-4 space-y-2" data-testid={`experience-bullets-${item.id}`}>
                {item.bullets.map((bullet, bulletIndex) => (
                  <li
                    key={`${item.id}-${bulletIndex}`}
                    className="flex gap-2 text-sm text-gray-700"
                    data-testid={`experience-bullet-${item.id}-${bulletIndex}`}
                  >
                    <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-wrap gap-2" data-testid={`experience-tags-${item.id}`}>
                {item.tags.map((tag) => (
                  <span
                    key={`${item.id}-${tag}`}
                    className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs text-blue-700"
                    data-testid={`experience-tag-${item.id}-${tag.toLowerCase().replace(/\s|&/g, "-")}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderProjectsSection = () => (
    <div data-testid="projects-section-content">
      <SectionHeading
        sectionId="projects"
        title="Products Built"
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-testid="project-cards-grid">
        {keyProjects.map((project) => (
          <Card
            key={project.id}
            className="overflow-hidden border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            data-testid={`project-card-${project.id}`}
          >
            <div
              className={`bg-gradient-to-br px-6 py-5 ${project.headerAccent || "from-blue-100/75 via-blue-100/45 to-blue-100/50"}`}
              data-testid={`project-title-panel-${project.id}`}
            >
              <div className="flex items-center gap-4">
                {project.logoUrl ? (
                  <img
                    src={project.logoUrl}
                    alt={project.logoAlt || project.title}
                    className="h-12 w-auto shrink-0 object-contain"
                    loading="lazy"
                    data-testid={`project-logo-${project.id}`}
                  />
                ) : null}
                <div className="min-w-0">
                  <CardTitle className="font-heading text-2xl text-gray-900" data-testid={`project-title-${project.id}`}>
                    {project.title}
                  </CardTitle>
                </div>
              </div>
            </div>
            <p
              className="border-b border-gray-100 px-6 py-3 text-xs uppercase tracking-[0.2em] text-gray-500"
              data-testid={`project-company-${project.id}`}
            >
              {project.company}
            </p>
            <CardContent className="pt-4">
              <p className="text-sm text-gray-700" data-testid={`project-summary-${project.id}`}>
                {project.summary}
              </p>

              <div className="mt-4 space-y-2" data-testid={`project-cards-${project.id}`}>
                {project.cards.map((entry, entryIndex) => (
                  <div
                    key={`${project.id}-${entry.type}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                    data-testid={`project-detail-${project.id}-${entryIndex}`}
                  >
                    <p
                      className="text-xs uppercase tracking-[0.2em] text-blue-700"
                      data-testid={`project-detail-type-${project.id}-${entryIndex}`}
                    >
                      {entry.type}
                    </p>
                    <p
                      className="mt-1 text-sm text-gray-700"
                      data-testid={`project-detail-text-${project.id}-${entryIndex}`}
                    >
                      {entry.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2" data-testid={`project-tags-${project.id}`}>
                {project.tags.map((tag) => (
                  <span
                    key={`${project.id}-${tag}`}
                    className="rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs text-blue-700"
                    data-testid={`project-tag-${project.id}-${tag.toLowerCase().replace(/\s|&/g, "-")}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {project.websiteUrl ? (
                <div className="mt-5">
                  <Button
                    asChild
                    variant="outline"
                    className="w-full rounded-full border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 sm:w-auto"
                    data-testid={`project-visit-cta-${project.id}`}
                  >
                    <a
                      href={project.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Visit {project.id === "ribbons" ? "ribbons.work" : "immerse.music"}
                    </a>
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderSkillsSection = () => (
    <div data-testid="skills-section-content">
      <SectionHeading
        sectionId="skills"
        title="Skills, Tools & Credentials"
        subtitle="Click each tile to reveal my corresponding skills."
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2" data-testid="skills-grid">
        {skillGroups.map((group) => (
          <SkillFlipCard key={group.id} group={group} />
        ))}

        <Card className="border-gray-200 bg-white xl:col-span-2" data-testid="education-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl" data-testid="education-title">
              Education
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-base font-semibold text-gray-900" data-testid="education-degree">
              {education.degree}
            </p>
            <div className="mt-2 flex items-stretch gap-4" data-testid="education-school-row">
              <div className="flex shrink-0 items-center" data-testid="education-logo-wrap">
                <img
                  src={education.logoUrl}
                  alt={education.logoAlt}
                  className="h-[88%] w-auto max-w-[3.96rem] object-contain"
                  loading="lazy"
                  data-testid="education-school-logo"
                />
              </div>
              <div className="flex flex-col justify-center" data-testid="education-school-details">
                <p className="text-sm text-gray-700" data-testid="education-school">
                  {education.school}
                </p>
                <p className="text-sm text-gray-600" data-testid="education-graduation">
                  {education.graduation}
                </p>
                <p className="text-sm text-gray-600" data-testid="education-extra">
                  {education.extra}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderContactSection = () => (
    <div data-testid="contact-section-content">
      <SectionHeading
        sectionId="contact"
        title="Contact Me"
        subtitle="Send your message directly through this secure form."
      />

      <Card className="border-gray-200 bg-white" data-testid="contact-form-card">
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={handleContactSubmit} data-testid="contact-form">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="contact-name" data-testid="contact-name-label">
                Name
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                value={contactForm.name}
                onChange={handleContactInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-blue-500"
                placeholder="Your name"
                required
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="contact-email" data-testid="contact-email-label">
                Email
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-blue-500"
                placeholder="you@company.com"
                required
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="contact-message" data-testid="contact-message-label">
                Message
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={contactForm.message}
                onChange={handleContactInputChange}
                className="min-h-32 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none transition-colors focus:border-blue-500"
                placeholder="How can I help?"
                required
                data-testid="contact-message-input"
              />
            </div>

            <div data-testid="contact-hcaptcha-wrapper">
              <HCaptcha
                sitekey={HCAPTCHA_SITE_KEY}
                reCaptchaCompat={false}
                onVerify={setHcaptchaToken}
                onExpire={() => setHcaptchaToken("")}
                ref={hcaptchaRef}
              />
            </div>

            <Button
              type="submit"
              className="rounded-full bg-blue-600 px-5 text-white hover:bg-blue-700"
              disabled={isContactSubmitting}
              data-testid="contact-submit-button"
            >
              {isContactSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>

          {contactStatus.type !== "idle" && (
            <div
              className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                contactStatus.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
              data-testid="contact-status-banner"
            >
              {contactStatus.message}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mt-10 w-full" data-testid="contact-outro-video-wrapper">
        <div
          className="w-full rounded-2xl border border-gray-200 bg-white p-3 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          data-testid="contact-outro-video-card"
        >
          <div
            className="relative aspect-video w-full overflow-hidden rounded-xl bg-gradient-to-b from-gray-100 to-gray-200 ring-1 ring-blue-100/60"
            data-testid="contact-outro-video-frame"
          >
            <video
              ref={outroVideoRef}
              className="h-full w-full object-contain object-center"
              controls
              controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
              disablePictureInPicture
              onContextMenu={(event) => event.preventDefault()}
              onPlay={() => setHasOutroStarted(true)}
              preload="metadata"
              poster={outroThumbnail.url || undefined}
              data-testid="contact-outro-video"
            >
              <source src={outroVideo.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {!hasOutroStarted && (
              <button
                type="button"
                onClick={handleOutroPlay}
                className="absolute inset-0 flex items-center justify-center bg-black/15"
                aria-label="Play goodbye video"
                data-testid="contact-outro-video-overlay-play"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-700/95 shadow-2xl transition-transform duration-300 hover:scale-105">
                  <Play className="ml-0.5 h-6 w-6 fill-white text-white" />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="resume-shell min-h-screen bg-gray-50 text-gray-900" data-testid="interactive-resume-page">
      <header
        className="sticky top-0 z-50 border-b border-gray-200/70 bg-white/80 backdrop-blur-xl"
        data-testid="sticky-header"
      >
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <div data-testid="header-branding">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-700" data-testid="header-brand-label">
              Product Owner Portfolio · Customer-Facing Digital Products
            </p>
            <p className="font-heading text-sm font-semibold sm:text-base" data-testid="header-candidate-name">
              {candidateProfile.name}
            </p>
          </div>

          <div className="flex items-center gap-3" data-testid="header-right-actions">
            <Button
              asChild
              variant="outline"
              className="hidden rounded-full border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white lg:inline-flex"
              data-testid="desktop-download-resume-pdf-button"
            >
              <a
                href={resumePdf.url}
                target="_blank"
                rel="noreferrer"
                download={resumePdf.fileName}
              >
                <Download className="h-4 w-4" />
                Download Resume
              </a>
            </Button>

            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="lg:hidden"
                  data-testid="mobile-section-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm" data-testid="mobile-section-menu-sheet">
                <SheetHeader>
                  <SheetTitle className="font-heading" data-testid="mobile-section-menu-title">
                    Resume Sections
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-6 space-y-2" data-testid="mobile-section-menu-items">
                  {resumeSectionRail.map((section) => {
                    const Icon = sectionIcons[section.id] || PlaySquare;
                    const isActive = section.id === activeSectionId;

                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => handleSectionCardClick(section.id)}
                        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                          isActive
                            ? "border-blue-500 bg-blue-50 text-blue-800"
                            : "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50"
                        }`}
                        data-testid={`mobile-section-menu-item-${section.id}`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="font-heading text-base">{section.title}</span>
                      </button>
                    );
                  })}

                  <a
                    href={resumePdf.url}
                    target="_blank"
                    rel="noreferrer"
                    download={resumePdf.fileName}
                    className="mt-4 flex w-full items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-left text-blue-800 transition-colors hover:bg-blue-100"
                    data-testid="mobile-download-resume-pdf-button"
                  >
                    <Download className="h-4 w-4" />
                    <span className="font-heading text-base">Download Resume</span>
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-5 py-6 sm:px-8" data-testid="main-content">
        <div
          className="grid grid-cols-1 gap-6 lg:h-[calc(100vh-108px)] lg:grid-cols-[272px_minmax(0,1fr)]"
          data-testid="resume-two-panel-layout"
        >
          <aside
            className="hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm lg:block lg:overflow-y-auto"
            data-testid="resume-left-section-rail"
          >
            <div className="mb-4" data-testid="section-rail-header">
              <h2 className="font-heading mt-2 text-2xl text-gray-900" data-testid="section-rail-title">
                Resume at a glance
              </h2>
            </div>

            <div className="space-y-3" data-testid="section-rail-cards">
              {resumeSectionRail.map((section) => {
                const Icon = sectionIcons[section.id] || PlaySquare;
                const isActive = section.id === activeSectionId;

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => handleSectionCardClick(section.id)}
                    className={`w-full rounded-xl border px-3 py-2.5 text-left transition-all ${
                      isActive
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40"
                    }`}
                    data-testid={`section-rail-card-${section.id}`}
                  >
                    <div className="flex items-center gap-3" data-testid={`section-rail-card-row-${section.id}`}>
                      <div
                        className={`rounded-lg p-2 ${isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
                        data-testid={`section-rail-icon-wrap-${section.id}`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1" data-testid={`section-rail-text-wrap-${section.id}`}>
                        <p className="font-heading text-base text-gray-900" data-testid={`section-rail-title-${section.id}`}>
                          {section.title}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <section
            className="min-h-0 lg:overflow-y-auto"
            ref={detailPanelRef}
            data-testid="resume-right-detail-panel"
          >
            <div className="space-y-4" data-testid="detail-panel-sections-stack">
              <section
                ref={registerSectionRef("welcome")}
                data-section-id="welcome"
                data-testid="detail-section-welcome"
                className={detailSectionClassName}
              >
                {renderWelcomeSection()}
              </section>

              <section
                ref={registerSectionRef("projects")}
                data-section-id="projects"
                data-testid="detail-section-projects"
                className={detailSectionClassName}
              >
                {renderProjectsSection()}
              </section>

              <section
                ref={registerSectionRef("experience")}
                data-section-id="experience"
                data-testid="detail-section-experience"
                className={detailSectionClassName}
              >
                {renderExperienceSection()}
              </section>

              <section
                ref={registerSectionRef("skills")}
                data-section-id="skills"
                data-testid="detail-section-skills"
                className={detailSectionClassName}
              >
                {renderSkillsSection()}
              </section>

              <section
                ref={registerSectionRef("contact")}
                data-section-id="contact"
                data-testid="detail-section-contact"
                className={detailSectionClassName}
              >
                {renderContactSection()}
              </section>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
